/**
 * Offline indicator (§39).
 *
 * §39: "Detect online/offline events but do not assume they are perfect."
 * `navigator.onLine` reports whether there is *a* network, not whether our
 * API is reachable — a captive portal is "online". So this is a hint, shown
 * as one, and nothing in the app branches on it: a request still has to fail
 * before we tell a child something did not work.
 */

import { useEffect, useState } from 'react';

import { useLanguage } from '@/features/language/LanguageProvider';

export function OfflineBanner() {
  const { t } = useLanguage();
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 bg-[var(--color-warning-soft)] px-4 py-2 text-center text-[length:var(--text-sm)] text-[var(--color-warning)]"
    >
      <span aria-hidden="true">📡 </span>
      {t('error.OFFLINE')}
    </div>
  );
}
